/**
 * DuitNow QR Code Generator with Dynamic Amount
 * Based on EMVCo QR Code Specification for Payment Systems
 * 
 * IMPORTANT: You need to get your merchant QR code from Touch 'n Go app first:
 * 1. Register as TNG merchant: https://touchngoewallet.onelink.me/8mmV/goecombelanjawanweb
 * 2. Go to TNG eWallet app > Merchant Dashboard > Get QR Code
 * 3. Copy the QR string (it starts with "00020101021...")
 * 4. Save it in your environment variables as MERCHANT_TNG_QR
 */

export class DuitNowQRGenerator {
  /**
   * Parse EMVCo QR code string
   * @param {string} qrString - Base merchant QR string from TNG
   * @returns {Object} Parsed QR data
   */
  static parseQR(qrString) {
    const data = {};
    let pos = 0;
    
    while (pos < qrString.length) {
      const id = qrString.substr(pos, 2);
      const length = parseInt(qrString.substr(pos + 2, 2), 10);
      const value = qrString.substr(pos + 4, length);
      
      data[id] = value;
      pos += 4 + length;
    }
    
    return data;
  }

  /**
   * Calculate CRC16-CCITT checksum for QR code
   * @param {string} data - QR string without checksum
   * @returns {string} 4-character hex checksum
   */
  static calculateCRC16(data) {
    let crc = 0xFFFF;
    const polynomial = 0x1021;
    
    for (let i = 0; i < data.length; i++) {
      crc ^= (data.charCodeAt(i) << 8);
      
      for (let j = 0; j < 8; j++) {
        if (crc & 0x8000) {
          crc = (crc << 1) ^ polynomial;
        } else {
          crc = crc << 1;
        }
      }
    }
    
    crc = crc & 0xFFFF;
    return crc.toString(16).toUpperCase().padStart(4, '0');
  }

  /**
   * Build TLV (Tag-Length-Value) format
   * @param {string} tag - 2-digit tag
   * @param {string} value - Value string
   * @returns {string} TLV formatted string
   */
  static buildTLV(tag, value) {
    const length = value.length.toString().padStart(2, '0');
    return `${tag}${length}${value}`;
  }

  /**
   * Generate dynamic QR code with amount
   * @param {string} merchantQR - Your base merchant QR string from TNG
   * @param {number} amount - Payment amount in MYR (e.g., 15.50)
   * @param {string} currency - Currency code (default: 458 for MYR)
   * @param {string} billNumber - Optional bill/order number
   * @returns {string} Dynamic QR code string with embedded amount
   */
  static generateDynamicQR(merchantQR, amount, currency = '458', billNumber = '') {
    try {
      // Parse the base merchant QR
      const parsed = this.parseQR(merchantQR);
      
      // EMVCo QR Code Data Objects:
      // 00 = Payload Format Indicator (always "01")
      // 01 = Point of Initiation Method ("11" for static, "12" for dynamic)
      // 26-51 = Merchant Account Information (varies by provider)
      // 52 = Merchant Category Code
      // 53 = Transaction Currency (458 = MYR)
      // 54 = Transaction Amount
      // 58 = Country Code (MY)
      // 59 = Merchant Name
      // 60 = Merchant City
      // 62 = Additional Data Field Template
      // 63 = CRC (checksum)
      
      let qrData = '';
      
      // 00: Payload Format Indicator
      qrData += this.buildTLV('00', parsed['00'] || '01');
      
      // 01: Point of Initiation Method (12 = dynamic with amount)
      qrData += this.buildTLV('01', '12');
      
      // 26-51: Merchant Account Information (keep original)
      for (let i = 26; i <= 51; i++) {
        const tag = i.toString().padStart(2, '0');
        if (parsed[tag]) {
          qrData += this.buildTLV(tag, parsed[tag]);
        }
      }
      
      // 52: Merchant Category Code (keep original if exists)
      if (parsed['52']) {
        qrData += this.buildTLV('52', parsed['52']);
      }
      
      // 53: Transaction Currency (458 = Malaysian Ringgit)
      qrData += this.buildTLV('53', currency);
      
      // 54: Transaction Amount (format: "15.50" without currency symbol)
      qrData += this.buildTLV('54', amount.toFixed(2));
      
      // 58: Country Code
      qrData += this.buildTLV('58', parsed['58'] || 'MY');
      
      // 59: Merchant Name (keep original)
      if (parsed['59']) {
        qrData += this.buildTLV('59', parsed['59']);
      }
      
      // 60: Merchant City (keep original)
      if (parsed['60']) {
        qrData += this.buildTLV('60', parsed['60']);
      }
      
      // 62: Additional Data Field Template (for bill number, reference, etc.)
      if (billNumber || parsed['62']) {
        let additionalData = '';
        
        // 01: Bill Number / Reference Number
        if (billNumber) {
          additionalData += this.buildTLV('01', billNumber);
        }
        
        // Add other additional fields from original QR if they exist
        if (parsed['62']) {
          const additionalParsed = this.parseQR(parsed['62']);
          for (const key in additionalParsed) {
            if (key !== '01' || !billNumber) { // Don't duplicate bill number
              additionalData += this.buildTLV(key, additionalParsed[key]);
            }
          }
        }
        
        qrData += this.buildTLV('62', additionalData);
      }
      
      // 63: CRC (calculated last)
      // CRC tag is 63, length is 04 (4 characters for the checksum)
      qrData += '6304'; // Tag 63, Length 04
      
      // Calculate CRC over everything including the '6304' placeholder
      const crc = this.calculateCRC16(qrData);
      
      // Append the actual CRC value (this gives us 6304XXXX)
      const finalQR = qrData + crc;
      
      console.log('QR before CRC:', qrData);
      console.log('Calculated CRC:', crc);
      console.log('Final QR:', finalQR);
      console.log('Final QR ends with:', finalQR.substring(finalQR.length - 8));
      
      return finalQR;
      
    } catch (error) {
      console.error('Error generating dynamic QR:', error);
      throw new Error('Failed to generate dynamic QR code');
    }
  }

  /**
   * Validate if QR string is valid EMVCo format
   * @param {string} qrString - QR code string
   * @returns {boolean} True if valid
   */
  static isValidQR(qrString) {
    try {
      if (!qrString || qrString.length < 10) return false;
      
      // Check if starts with "00020" (EMVCo format, tag 00 = Payload Format Indicator)
      if (!qrString.startsWith('00020')) return false;
      
      // Check if has CRC field (tag 63)
      if (!qrString.includes('6304')) return false;
      
      // Basic structure validation - should have required tags
      // Tag 00: Payload Format, 01: Point of Initiation, 26/27-51: Merchant Account
      const hasRequiredTags = qrString.includes('0002') && qrString.length >= 50;
      
      return hasRequiredTags;
      
    } catch (error) {
      console.error('QR validation error:', error);
      return false;
    }
  }

  /**
   * Extract amount from dynamic QR code
   * @param {string} qrString - QR code string
   * @returns {number|null} Amount or null if not found
   */
  static extractAmount(qrString) {
    try {
      const parsed = this.parseQR(qrString);
      return parsed['54'] ? parseFloat(parsed['54']) : null;
    } catch (error) {
      return null;
    }
  }
}

export default DuitNowQRGenerator;
